import { AxiosResponse } from "axios";
import { axiosInstance } from "./axiosInstance";
import {
	EnrollmentRequestDetailsDTO, EnrollmentRequestListDTO, ExistingStudentDTO, RequestCatalogsDTO, ReviewersDTO
} from "../../models/EnrollmentHistoryDTO";

const base = (campusId: number) => `/admin/campus/${campusId}/enrollmentRequests`;

export interface RequestPayload {
	modeId?: number;
	studentId?: number | null;
	studentData?: any;
	scheduleId?: number | null;
	advisors?: { userId: string; name: string; sharePercentage?: number | null }[];
	notes?: string | null;
	commercialTerms?: string | null;
	payment?: any;
	submit?: boolean;
	/** Completar una persona de una pre-solicitud. */
	prerequestPersonId?: number;
	/** Guardado automático: la versión que se tenía; si otro equipo guardó después, el servidor no la pisa. */
	version?: number | null;
}

/** Solicitudes de matrícula (Fase 1). */
export class EnrollmentRequestsService {
	filterOptions = async (campusId: number) =>
		axiosInstance.get<any>(`${base(campusId)}/_filterOptions`);
	list = async (campusId: number, criteria: any, perPage: number, page: number): Promise<AxiosResponse<EnrollmentRequestListDTO>> =>
		axiosInstance.get<EnrollmentRequestListDTO>(`${base(campusId)}?perPage=${perPage}&page=${page}&criteria=${encodeURIComponent(JSON.stringify(criteria || {}))}`);

	catalogs = async (campusId: number): Promise<AxiosResponse<RequestCatalogsDTO>> => axiosInstance.get<RequestCatalogsDTO>(`${base(campusId)}/_catalogs`);

	details = async (campusId: number, requestId: number): Promise<AxiosResponse<EnrollmentRequestDetailsDTO>> =>
		axiosInstance.get<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_details`);

	create = async (campusId: number, payload: RequestPayload): Promise<AxiosResponse<EnrollmentRequestDetailsDTO>> =>
		axiosInstance.post<EnrollmentRequestDetailsDTO>(base(campusId), payload);

	update = async (campusId: number, requestId: number, payload: RequestPayload): Promise<AxiosResponse<EnrollmentRequestDetailsDTO>> =>
		axiosInstance.put<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}`, payload);

	submit = async (campusId: number, requestId: number, tutorIndex?: number) => axiosInstance.put<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_submit`, { tutorIndex });
	approve = async (campusId: number, requestId: number, tutorIndex?: number) => axiosInstance.put<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_approve`, { tutorIndex });
	/** Texto + imágenes (capturas) en un solo envío multipart. */
	private conArchivos = (campos: { [k: string]: any }, files?: File[]) => {
		const form = new FormData();
		Object.keys(campos).forEach((k) => { if (campos[k] !== undefined && campos[k] !== null) form.append(k, String(campos[k])); });
		(files || []).forEach((f) => form.append("files[]", f, f.name));
		return form;
	};
	private multipart = { headers: { "Content-Type": "multipart/form-data" } };

	returnToSales = async (campusId: number, requestId: number, comment: string, files?: File[]) =>
		axiosInstance.post<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_return`, this.conArchivos({ comment }, files), this.multipart);
	markError = async (campusId: number, requestId: number, reason: string) =>
		axiosInstance.post<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_markError`, { reason });
	reassign = async (campusId: number, requestId: number, userId: number, comment: string, files?: File[]) =>
		axiosInstance.post<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_reassign`, this.conArchivos({ userId, comment }, files), this.multipart);
	comment = async (campusId: number, requestId: number, body: string, files?: File[]) =>
		axiosInstance.post<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_comments`, this.conArchivos({ body }, files), this.multipart);
	resendToCaja = async (campusId: number, requestId: number, comment: string, files: File[]) =>
		axiosInstance.post<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_resendToCaja`, this.conArchivos({ comment }, files), this.multipart);
	changeSchedule = async (campusId: number, requestId: number, fromScheduleId: number, toScheduleId: number, comment: string, files: File[]) =>
		axiosInstance.post<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_changeSchedule`, this.conArchivos({ fromScheduleId, toScheduleId, comment }, files), this.multipart);
	commentAttachment = async (campusId: number, requestId: number, commentId: number, index: number) =>
		axiosInstance.get<Blob>(`${base(campusId)}/${requestId}/_commentAttachment/${commentId}/${index}`, { responseType: "blob" });

	uploadDocument = async (campusId: number, requestId: number, typeId: number, file: File) => {
		const form = new FormData();
		form.append("typeId", String(typeId));
		form.append("file", file);
		return axiosInstance.post<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_documents`, form, { headers: { "Content-Type": "multipart/form-data" } });
	};
	/** Identificación leída con IA (19-oct-2026): en persona, revisión de Admisiones y "usar los datos de la identificación". */
	leerIdentificacion = async (campusId: number, file: File) => {
		const f = new FormData();
		f.append("file", file);
		return axiosInstance.post<LecturaIdentificacion>(`${base(campusId)}/_leerIdentificacion`, f, { headers: { "Content-Type": "multipart/form-data" } });
	};
	/** "Usar el celular": QR de un solo uso para tomar la foto con el celular y bajarla aquí (204 = todavía no llega). */
	capturaCrear = async (campusId: number, tipo: "id_alumno" | "id_tutor" | "comprobante") =>
		axiosInstance.post<{ token: string; url: string; vence: string; titulo: string }>(`${base(campusId)}/_captura`, { tipo });
	capturaTomar = async (campusId: number, token: string) =>
		axiosInstance.get<Blob>(`${base(campusId)}/_captura/${token}`, { responseType: "blob" });
	revisarIdentificacion = async (campusId: number, requestId: number) =>
		axiosInstance.get<RevisionIdentificacion>(`${base(campusId)}/${requestId}/_identificacion`);
	usarIdentificacion = async (campusId: number, requestId: number) =>
		axiosInstance.put<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_usarIdentificacion`);
	downloadDocument = async (campusId: number, requestId: number, documentId: number) =>
		axiosInstance.get<Blob>(`${base(campusId)}/${requestId}/_documents/${documentId}`, { responseType: "blob" });
	downloadContract = async (campusId: number, requestId: number, contractId: number, signed = false) =>
		axiosInstance.get<Blob>(`${base(campusId)}/${requestId}/_contracts/${contractId}` + (signed ? "?signed=1" : ""), { responseType: "blob" });
	downloadContractVersion = async (campusId: number, requestId: number, contractId: number, versionId: number) =>
		axiosInstance.get<Blob>(`${base(campusId)}/${requestId}/_contracts/${contractId}/_versions/${versionId}`, { responseType: "blob" });
	uploadSignedContract = async (campusId: number, requestId: number, contractId: number, file: File) => {
		const form = new FormData();
		form.append("file", file);
		return axiosInstance.post<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_contracts/${contractId}/_signed`, form, { headers: { "Content-Type": "multipart/form-data" } });
	};
	regenerateContract = async (campusId: number, requestId: number, contractId: number, tutorIndex?: number) =>
		axiosInstance.post<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_contracts/${contractId}/_regenerate`, { tutorIndex });
	deleteDocument = async (campusId: number, requestId: number, documentId: number) =>
		axiosInstance.delete<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_documents/${documentId}`);

	reviewers = async (campusId: number): Promise<AxiosResponse<ReviewersDTO>> => axiosInstance.get<ReviewersDTO>(`${base(campusId)}/_reviewers`);
	saveReviewer = async (campusId: number, userId: number, active: boolean) => axiosInstance.put<ReviewersDTO>(`${base(campusId)}/_reviewers`, { userId, active });
	findStudents = async (campusId: number, q: string): Promise<AxiosResponse<ExistingStudentDTO[]>> =>
		axiosInstance.get<ExistingStudentDTO[]>(`${base(campusId)}/_findStudents?q=${encodeURIComponent(q)}`);

	/** Matrículas → Contratos (2026-10-15): pendientes, enviados, abiertos y firmados. */
	contratos = async (campusId: number, f: { estado?: string; desde?: string; hasta?: string; q?: string; modo?: number; vendio?: number }, perPage: number, page: number): Promise<AxiosResponse<ContratosTablero>> =>
		axiosInstance.get<ContratosTablero>(`${base(campusId)}/_contratos`, { params: { ...f, perPage, page } });
	enviarContratoFirma = async (campusId: number, contractId: number) =>
		axiosInstance.post<{ ok: boolean; modo?: string; enlace?: string; error?: string }>(`${base(campusId)}/_contratos/${contractId}/_enviar`);
	enlaceContratoFirma = async (campusId: number, contractId: number) =>
		axiosInstance.post<{ enlace: string }>(`${base(campusId)}/_contratos/${contractId}/_enlace`);
	/** El contrato firmado en papel (foto o PDF): la IA revisa que traiga la firma. */
	subirContratoPapel = async (campusId: number, contractId: number, file: File) => {
		const form = new FormData();
		form.append("file", file);
		return axiosInstance.post<{ ok: boolean }>(`${base(campusId)}/_contratos/${contractId}/_papel`, form, { headers: { "Content-Type": "multipart/form-data" } });
	};
	confirmarContratoPapel = async (campusId: number, contractId: number) =>
		axiosInstance.post<{ ok: boolean }>(`${base(campusId)}/_contratos/${contractId}/_confirmar`);
}

export interface LecturaIdentificacion {
	disponible?: boolean;
	leida: boolean;
	aviso: string | null;
	campos?: { firstName?: string; lastName?: string; birthday?: string; genderId?: number; identificationTypeId?: number; identificationCode?: string; nationalityId?: number };
	nombreCompleto?: string;
	confianza?: "alta" | "media" | "baja";
	dudas?: string[];
}
export interface RevisionIdentificacion {
	disponible: boolean;
	alumno: (LecturaIdentificacion & { documentId: number; diferencias: { campo: string; texto: string }[]; editable: boolean }) | null;
	tutor: (LecturaIdentificacion & { documentId: number; diferencias: { campo: string; texto: string }[] }) | null;
}
export interface ContratoFila {
	id: number; requestId: number | null; studentId: number; alumno: string; correlativo: string | null; curso: string | null; fecha: string;
	vendio: string | null; modo: "Digital" | "Presencial" | null; estado: string; estadoNombre: string; enviadoEl: string | null; modo_envio: string | null;
	error: string | null; abiertoEl: string | null; vecesAbierto: number; firmadoEl: string | null; firmante: string | null; relacion: "alumno" | "tutor" | null;
	metodo: "firma" | "acepto" | "papel" | null; dispositivo: string | null; ip: string | null; subio: string | null;
	ia: "pendiente" | "verificado" | "revisar" | "no_firmado" | null; iaNombre: string | null; iaNota: string | null;
	enlace: string | null; documentoId: number | null;
}
export interface ContratosTablero {
	data: ContratoFila[]; total: number; page: number; perPage: number; conteos: { [estado: string]: number }; estados: { [estado: string]: string };
	vendedoras: { id: number; nombre: string }[];
}
